import React, { Fragment, Suspense, useState } from "react";
import { FormAddToFolderModal } from "./components/form-folders/FormAddToFolderModal";
import { WarningModal } from "../../components/WarningModal";
import { FormDeleteWarningModal } from "./components/FormDeleteWarningModal";
import { EntityAssignModal } from "../../components/EntityAssignModal";
import { FormReadModal } from "./components/FormReadModal";
import { AtiraToast } from "../../utils/AtiraToast";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { formActions } from "../../redux/forms/form.slice";
import { thresholdActions } from "../../redux/threshold/threshold.slice";
import { useAtiraFormContext } from "./context/form-context";
import { userSliceSelectors } from "../../redux/user/user.selector";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import { FormAssignDto } from "../../model/form/dto/AssignFormDto";
import { Status } from "../../model/shared/enum/Status.enum";
import { useFormContext } from "react-hook-form";
import { GetUserFormsDto } from "../../model/form/dto/GetUserFormsDto";
import { FormsFolderUpdateModal } from "./components/form-folders/FormsFolderUpdateModal";
import { useLocation, useNavigate } from "react-router-dom";
import { SpinnerFullScreen } from "../../components/SpinnerFullScreen";
import { TestIDs } from "../../tests/types/testIds";
import { FormActivityModal } from "./components/FormActivityModal";
import { TabKeys } from "./types/FormTabs.enum";

const FormCreateEditDrawer = React.lazy(() => import("./FormCreateEditDrawer"));
const FormReCaptchaConfigureDrawer = React.lazy(
  () => import("./components/form-builder/FormReCaptchaConfigureDrawer"),
);
const FormAutoAssignDrawer = React.lazy(
  () => import("./components/form-builder/FormAutoAssignDrawer"),
);
const FromShareDrawer = React.lazy(
  () => import("./components/form-share/FormShareDrawer"),
);
const FormCalendarDrawer = React.lazy(
  () => import("./components/FormCalendarDrawer"),
);

export const FormDrawers: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { getValues } = useFormContext<GetUserFormsDto>();
  const {
    selectedForm,
    selectedFolder,
    setHighlightedFormId,
    //
    formRemoveFromFolderVisible,
    setFormRemoveFromFolderVisible,
    formDuplicateVisible,
    setFormDuplicateVisible,
    setFormDeleteVisible,
    formUpdateStatusVisible,
    setFormUpdateStatusVisible,
    formAssignVisible,
    setFormAssignVisible,
  } = useAtiraFormContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const isOnFolderFormsPage = pathname.includes("/forms/folder-");

  const deleteForm = async () => {
    try {
      if (!selectedForm) {
        return;
      }

      setLoading(true);
      await dispatch(
        formActions.deleteForm({ formId: selectedForm?._id!, userId }),
      ).unwrap();
      if (isOnFolderFormsPage) {
        await dispatch(formActions.getFolders({ userId })).unwrap();
      } else {
        await dispatch(formActions.getForms({ userId })).unwrap();
      }
      await dispatch(thresholdActions.getThresholds({ userId })).unwrap();
      setFormDeleteVisible(false);
      AtiraToast.success(t("forms.form.delete.success"));
    } catch (error: any) {
      console.log(error);
      AtiraToast.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const cloneForm = async () => {
    try {
      if (!selectedForm?._id) {
        return;
      }

      setLoading(true);
      const clonedForm = await dispatch(
        formActions.cloneForm({ formId: selectedForm._id, userId }),
      ).unwrap();

      setHighlightedFormId(clonedForm._id);

      if (isOnFolderFormsPage) {
        await dispatch(formActions.getFolders({ userId })).unwrap();
      } else {
        await dispatch(
          formActions.getForms({
            userId,
            ...omitBy(getValues(), isNil),
          }),
        ).unwrap();
      }

      await dispatch(thresholdActions.getThresholds({ userId })).unwrap();
      setFormDuplicateVisible(false);
      AtiraToast.success(t("forms.duplicate.success"));
    } catch (error: any) {
      console.log(error);
      AtiraToast.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const updateFormStatus = async () => {
    try {
      setLoading(true);

      if (!selectedForm) {
        return;
      }

      await dispatch(
        formActions.updateForm({
          formId: selectedForm._id!,
          status:
            selectedForm?.status === Status.ACTIVE
              ? Status.INACTIVE
              : Status.ACTIVE,
          userId,
        }),
      ).unwrap();
      if (isOnFolderFormsPage) {
        await dispatch(
          formActions.getFolders({
            userId,
          }),
        ).unwrap();
      } else {
        await dispatch(
          formActions.getForms({
            userId,
            ...omitBy(getValues(), isNil),
          }),
        ).unwrap();
      }

      setFormUpdateStatusVisible(false);
      AtiraToast.success(
        t(
          `forms.actions.status.${selectedForm?.status?.toLowerCase()}.success`,
          {
            name: selectedForm.title,
          },
        ),
      );
    } catch (error: any) {
      console.log(error);
      AtiraToast.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const onAssign = async (ids: string[]) => {
    try {
      const updateDto: FormAssignDto = {
        assigneeIds: ids,
        formId: selectedForm?._id!,
        userId,
      };

      await dispatch(formActions.assignForm(updateDto)).unwrap();
      if (isOnFolderFormsPage) {
        await dispatch(formActions.getFolders({ userId })).unwrap();
      } else {
        await dispatch(formActions.getForms({ userId })).unwrap();
      }
      AtiraToast.success(t("form.assign.modal.assign-success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const onRemoveFromFolder = async () => {
    try {
      if (!selectedForm?._id || !selectedFolder?._id) {
        return;
      }

      setLoading(true);

      await dispatch(
        formActions.addRemoveFormToFolder({
          formId: selectedForm._id,
          formFolderId: selectedFolder._id,
          mode: "remove",
          userId,
        }),
      ).unwrap();
      await dispatch(formActions.getFolders({ userId })).unwrap();

      setFormRemoveFromFolderVisible(false);

      AtiraToast.success(t("forms.removeFromFolder.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Suspense fallback={<SpinnerFullScreen />}>
        <FormCreateEditDrawer />
      </Suspense>

      <Suspense fallback={<SpinnerFullScreen />}>
        <FormReCaptchaConfigureDrawer />
      </Suspense>

      <Suspense fallback={<SpinnerFullScreen />}>
        <FromShareDrawer />
      </Suspense>

      <Suspense fallback={<SpinnerFullScreen />}>
        <FormAutoAssignDrawer />
      </Suspense>

      <Suspense fallback={<SpinnerFullScreen />}>
        <FormCalendarDrawer />
      </Suspense>

      <FormReadModal />
      <FormActivityModal />

      <EntityAssignModal
        isOpen={formAssignVisible}
        onClose={() => setFormAssignVisible(false)}
        onConfirm={onAssign}
        entity={selectedForm}
      />

      <FormDeleteWarningModal loading={loading} onConfirm={deleteForm} />

      <WarningModal
        title={t(`${t("common.duplicate")}: ${selectedForm?.title}`)}
        isOpen={formDuplicateVisible}
        onClose={() => setFormDuplicateVisible(false)}
        loading={loading}
        onConfirm={cloneForm}
        description={t("forms.duplicate.modal.main_title")}
        confirmButtonDataTestId={TestIDs.forms.form.actions.cloneModalConfirm}
      />

      <WarningModal
        title={t(
          `forms.actions.status.${selectedForm?.status?.toLowerCase()}.modal.title`,
          {
            name: selectedForm?.title,
          },
        )}
        isOpen={formUpdateStatusVisible}
        onClose={() => setFormUpdateStatusVisible(false)}
        loading={loading}
        onConfirm={updateFormStatus}
        description={t(
          `forms.actions.status.${selectedForm?.status?.toLowerCase()}.modal.description`,
        )}
        confirmButtonText={t(
          `forms.actions.status.${selectedForm?.status?.toLowerCase()}.modal.button.yes`,
        )}
        denyButtonText={t("common.cancel")}
      />

      <FormAddToFolderModal />

      <FormsFolderUpdateModal />

      <WarningModal
        title={t("forms.removeFromFolder.title")}
        description={t("forms.removeFromFolder.description")}
        isOpen={formRemoveFromFolderVisible}
        onConfirm={onRemoveFromFolder}
        onClose={() => setFormRemoveFromFolderVisible(false)}
        loading={loading}
        confirmButtonDataTestId={
          TestIDs.forms.form.removeFromFolder.modal.button
        }
      />
    </Fragment>
  );
};
